import type { ComponentProps } from 'react';
import React from 'react';
import type { ViewStyle } from 'react-native';
import { Platform, StyleSheet, View } from 'react-native';
import type { IconName } from '../Icon';
import { Icon, IconLink } from '../Icon';
import { theme } from '@garnish/constants';
import { usePressableState } from '../../hooks';
import { WithTooltip } from '../WithTooltip';

export const ShareIcon = ({
  shareTooltipLabel = 'Copied link',
  shareBtnLabel = 'Share',
  iconName = 'IconShare2',
  placement,
  style,
  onShare,
}: ShareIconProps) => {
  const iconLinkRef = React.useRef(null);
  const { isHovered, isFocused } = usePressableState(iconLinkRef);

  const iconProps = {
    width: 24,
    height: 24,
    name: iconName,
    color: theme.colors.BLACK,
  } as const;

  const iconLinkStyle = [
    styles.shareIconLink,
    (isHovered || isFocused) && styles.shareIconLinkHover,
  ];

  if (!onShare) return null;

  if (Platform.OS === 'web') {
    return (
      <WithTooltip
        testID="product-share-web"
        text={shareTooltipLabel}
        toggleAccessibilityLabel={shareBtnLabel}
        onShow={onShare}
        autoDismissTimeout={1000}
        placement={placement}
      >
        <View ref={iconLinkRef} style={[iconLinkStyle, style]}>
          <Icon {...iconProps} />
        </View>
      </WithTooltip>
    );
  }

  return (
    <IconLink
      {...iconProps}
      style={style}
      testID="product-share"
      onPress={onShare}
    />
  );
};

type ShareIconProps = {
  readonly shareTooltipLabel?: string;
  readonly shareBtnLabel?: string;
  readonly iconName?: IconName;
  readonly style?: ViewStyle;
  readonly placement?: ComponentProps<typeof WithTooltip>['placement'];
  readonly onShare?: () => void;
};

const styles = StyleSheet.create({
  shareIconLink: {
    borderRadius: 1000,
  },
  shareIconLinkHover: {
    backgroundColor: theme.colors.OPACITY.GREEN_1.ALMOST_TRANSPARENT,
  },
});
